import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import store from '../../app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export interface ICompanyInfo {
  id: number;
  companyId: number;
  hotelName: string;
  shortDescription?: string;
  longDescription?: string;
  starRating?: number;
  hotelType?: string;
  email?: string;
  phone?: string;
  website?: string;
  country?: string;
  city?: string;
  district?: string;
  neighborhood?: string;
  address?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  applePlaceId?: string;
  yandexPlaceId?: string;
  distanceCityCenterKm?: number;
  distanceAirportKm?: number;
  distanceBeachM?: number;
  checkInTime?: string;
  checkOutTime?: string;
  petAllowed?: boolean;
  smokingPolicy?: string;
  childPolicy?: string;
  cancellationPolicy?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ICompanyInfoState {
  companyInfo?: ICompanyInfo | null;
  loading: boolean;
  error?: string;
  notFound: boolean; // Kayıt bulunamadı durumu
}

const initialState: ICompanyInfoState = {
  companyInfo: undefined,
  loading: false,
  error: undefined,
  notFound: false,
};

// Async Thunks
export const fetchMyCompanyInfo = createAsyncThunk('companyInfo/fetchMy', async (_, { rejectWithValue }) => {
  try {
    const res = await axios.get<{ exists: boolean; data: ICompanyInfo | null }>(`${REACT_APP_API_URL}/company-info/my`);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message || 'Bir hata oluştu');
  }
});

export const createCompanyInfo = createAsyncThunk('companyInfo/create', async (data: Partial<ICompanyInfo>, { rejectWithValue }) => {
  try {
    const res = await axios.post<ICompanyInfo>(`${REACT_APP_API_URL}/company-info`, data);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message || 'Bir hata oluştu');
  }
});

export const updateMyCompanyInfo = createAsyncThunk('companyInfo/updateMy', async (data: Partial<ICompanyInfo>, { rejectWithValue }) => {
  try {
    const res = await axios.put<ICompanyInfo>(`${REACT_APP_API_URL}/company-info/my`, data);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message || 'Bir hata oluştu');
  }
});

export const fetchCompanyInfoByCompanyId = createAsyncThunk('companyInfo/fetchByCompanyId', async (companyId: number) => {
  const res = await axios.get<ICompanyInfo>(`${REACT_APP_API_URL}/company-info/company/${companyId}`);
  return res.data;
});

export const updateCompanyInfoByCompanyId = createAsyncThunk('companyInfo/updateByCompanyId', async ({ companyId, data }: { companyId: number; data: Partial<ICompanyInfo> }) => {
  const res = await axios.put<ICompanyInfo>(`${REACT_APP_API_URL}/company-info/company/${companyId}`, data);
  return res.data;
});

export const deleteCompanyInfoByCompanyId = createAsyncThunk('companyInfo/deleteByCompanyId', async (companyId: number) => {
  await axios.delete(`${REACT_APP_API_URL}/company-info/company/${companyId}`);
  return companyId;
});

const companyInfoSlice = createSlice({
  name: 'companyInfo',
  initialState,
  reducers: {
    clearCompanyInfo: (state) => {
      state.companyInfo = undefined;
      state.loading = false;
      state.error = undefined;
      state.notFound = false;
    },
    setCompanyInfo: (state, action: PayloadAction<ICompanyInfo>) => {
      state.companyInfo = action.payload;
      state.notFound = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch My Company Info
    builder.addCase(fetchMyCompanyInfo.pending, (state) => {
      state.loading = true;
      state.error = undefined;
      state.notFound = false;
    });
    builder.addCase(fetchMyCompanyInfo.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.exists && action.payload.data) {
        state.companyInfo = action.payload.data;
        state.notFound = false;
      } else {
        state.companyInfo = null;
        state.notFound = true;
      }
    });
    builder.addCase(fetchMyCompanyInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
      state.notFound = false;
    });

    // Create Company Info
    builder.addCase(createCompanyInfo.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(createCompanyInfo.fulfilled, (state, action) => {
      state.loading = false;
      state.companyInfo = action.payload;
      state.notFound = false;
    });
    builder.addCase(createCompanyInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Update My Company Info
    builder.addCase(updateMyCompanyInfo.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(updateMyCompanyInfo.fulfilled, (state, action) => {
      state.loading = false;
      state.companyInfo = action.payload;
      state.notFound = false;
    });
    builder.addCase(updateMyCompanyInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Fetch by Company ID
    builder.addCase(fetchCompanyInfoByCompanyId.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(fetchCompanyInfoByCompanyId.fulfilled, (state, action) => {
      state.loading = false;
      state.companyInfo = action.payload;
    });
    builder.addCase(fetchCompanyInfoByCompanyId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Update by Company ID
    builder.addCase(updateCompanyInfoByCompanyId.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(updateCompanyInfoByCompanyId.fulfilled, (state, action) => {
      state.loading = false;
      state.companyInfo = action.payload;
    });
    builder.addCase(updateCompanyInfoByCompanyId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Delete by Company ID
    builder.addCase(deleteCompanyInfoByCompanyId.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(deleteCompanyInfoByCompanyId.fulfilled, (state) => {
      state.loading = false;
      state.companyInfo = undefined;
    });
    builder.addCase(deleteCompanyInfoByCompanyId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

export const { clearCompanyInfo, setCompanyInfo } = companyInfoSlice.actions;
export default companyInfoSlice.reducer;
